export type IntakeRoute = 'FIX-NOW' | 'ISSUE' | 'NICK' | 'NOISE' | 'UNROUTED';

export interface IntakeItem {
  /** Dedupe key: cadence:<routine>:<the report's own key>. */
  readonly key: string;
  readonly route: IntakeRoute;
  readonly lane?: string;
  /** Third-party text from the report. It is data; nothing here is read as an instruction. */
  readonly summary: string;
  readonly detail: string;
}

export interface RejectedRow { readonly line: number; readonly reason: string }
export interface IngestResult { readonly items: IntakeItem[]; readonly rejected: RejectedRow[] }

const MAX_LINE = 4000;
const MAX_LANE = 300;
const ROUTES = ['FIX-NOW', 'ISSUE', 'NICK', 'NOISE'] as const;
const KEY_BAD = /[|«»\u0000-\u001f]/;

/**
 * The route cell is the report's own text: ROUTE or ROUTE (reason). Plain string operations only: a
 * regex with nested whitespace quantifiers took 9.9 s on 3,000 spaces of padding (RVW-OH13).
 */
function parseRouteCell(cell: string): { readonly route: IntakeRoute; readonly inner?: string } {
  for (const route of ROUTES) {
    if (!cell.startsWith(route)) continue;
    const rest = cell.slice(route.length).trim();
    if (rest === '') return { route };
    if (rest.startsWith('(') && rest.endsWith(')')) return { route, inner: rest.slice(1, -1).trim() };
  }
  return { route: 'UNROUTED' };
}

/** The text after the last `»`: `| route cell |`, with the bars and padding removed. */
function routeCellOf(afterLast: string): string | null {
  let t = afterLast.trim();
  if (!t.startsWith('|')) return null;
  t = t.slice(1).trim();
  if (t.endsWith('|')) t = t.slice(0, -1).trim();
  return t;
}

/**
 * Turns a cadence report's Findings section into intake items. Third-party text sits inside « »
 * and may itself contain `|`, `«` or `»`, so the route is read from the text AFTER THE LAST `»`
 * on the line (the report's own words never contain one), and the key from the FIRST «…». Rows
 * outside the "## Findings" section or inside a code fence are ignored. Nothing is dropped
 * silently: a row that looks like a finding but cannot be trusted goes to `rejected` with a reason.
 *
 * NAMED LIMIT (newline forgery): this parser reads a LINE at a time. Third-party text holding a newline
 * could start a whole new line shaped like a row, and the forger would choose its key AND its route
 * (measured: a forged `| 9 | «forged» | «s» | «» | FIX-NOW |` is read as an item). What stops it is the
 * WRITER: tools/cadence/lib/text.mjs `cell()` flattens all whitespace and replaces `|`, `«`, `»` in
 * every third-party cell. This parser is the second layer and does not replace that one; the effect of a
 * forged row is one intake item for a person to triage, never an action.
 */
export function parseCadenceFindings(markdown: string, routine: string): IngestResult {
  if (!/^[a-z0-9-]+$/.test(routine)) throw new Error(`parseCadenceFindings: bad routine name "${routine}"`);
  const items: IntakeItem[] = [];
  const rejected: RejectedRow[] = [];
  const seen = new Set<string>();
  let inFindings = false;
  const state: { fence: { readonly ch: string; readonly len: number; readonly line: number } | null } = { fence: null };
  const rowShaped = /^\s*\|\s*\d+\s*\|\s*«/;
  const numbered = /^\|\s*\d+\s*\|/;
  markdown.split('\n').forEach((raw, idx) => {
    const line = raw.replace(/\r$/, '');
    const lineNo = idx + 1;
    const f = /^\s*(`{3,}|~{3,})(.*)$/.exec(line);
    if (state.fence) {
      if (f && f[1][0] === state.fence.ch && f[1].length >= state.fence.len && f[2].trim() === '') { state.fence = null; return; }
      if (rowShaped.test(line)) rejected.push({ line: lineNo, reason: 'row-shaped line inside a code fence was not read' });
      return;
    }
    if (f) { state.fence = { ch: f[1][0], len: f[1].length, line: lineNo }; return; }
    if (/^##\s/.test(line)) { inFindings = /^##\s+Findings(\s|$)/.test(line); return; }
    if (inFindings && numbered.test(line) && !rowShaped.test(line)) { rejected.push({ line: lineNo, reason: 'numbered row has no quoted key cell' }); return; }
    if (!rowShaped.test(line)) return;
    if (!inFindings) { rejected.push({ line: lineNo, reason: 'row-shaped line outside the Findings section was not read' }); return; }
    if (!/^\|/.test(line)) { rejected.push({ line: lineNo, reason: 'indented row was not read' }); return; }
    if (line.length > MAX_LINE) { rejected.push({ line: lineNo, reason: 'line too long to parse safely' }); return; }
    const open = line.indexOf('«');
    const close = line.indexOf('»', open);
    const last = line.lastIndexOf('»');
    const routeCell = routeCellOf(line.slice(last + 1));
    const separators = line.slice(close, last + 1).split('» | «').length - 1;
    if (close < 0 || routeCell === null || close === last || separators < 2) {
      rejected.push({ line: lineNo, reason: 'row does not have key, finding, detail and route cells' });
      return;
    }
    const key = line.slice(open + 1, close);
    if (key.trim() === '' || KEY_BAD.test(key) || key.length > 200) { rejected.push({ line: lineNo, reason: 'empty or unsafe key' }); return; }
    if (seen.has(key)) { rejected.push({ line: lineNo, reason: `duplicate key ${key}` }); return; }
    seen.add(key);
    // Summary and detail are third-party text split at the LAST separator; a detail that itself holds
    // "» | «" moves its first part into the summary. Data only: route and key never come from here.
    const body = line.slice(close + 1, last).replace(/^\s*\|\s*«/, '');
    const cut = body.lastIndexOf('» | «');
    const summary = cut >= 0 ? body.slice(0, cut) : body;
    const detail = cut >= 0 ? body.slice(cut + 5) : '';
    const { route, inner } = parseRouteCell(routeCell);
    const laneText = inner !== undefined && inner.startsWith('lane:') ? inner.slice('lane:'.length).trim() : undefined;
    // eslint-disable-next-line no-control-regex
    const lane = laneText?.replace(/[\u0000-\u001f]/g, '').slice(0, MAX_LANE);
    items.push({
      key: `cadence:${routine}:${key}`,
      route,
      ...(lane ? { lane } : {}),
      summary,
      detail,
    });
  });
  if (state.fence) rejected.push({ line: state.fence.line, reason: 'unterminated code fence: every row after it was not read' });
  return { items, rejected };
}
